import { create } from "zustand";
import { MeResponse, User, UserPayload, UserRole } from "../types/user";
import { Brand } from "../types/brand";
import { getUserPayloadFromToken } from "../utils";

type UserState = {
  user: UserPayload | null,
  userRole: UserRole | null,
  permissions: string[],
  /** Brand the user is scoped to (null = global/root scope). Source: /auth/me. */
  brandId: string | null,
  /** True once /auth/me has resolved, so permission/scope checks are reliable. */
  meLoaded: boolean,
  accessToken: string | null,
  refreshToken: string | null,
  userProfile: User | null,
  userAdminBrand: Brand | null,
}

type UserActions = {
  setUser: (user: UserPayload | null) => void,
  setUserRole: (role: UserRole) => void,
  setPermissions: (permissions: string[]) => void,
  setMe: (me: MeResponse) => void,
  setUserProfile: (user: User | null) => void,
  setUserAdminBrand: (brand: Brand | null) => void,
  setAccessToken: (token: string | null) => void,
  setRefreshToken: (token: string | null) => void,
  logout: () => void,
}

const initialUserState: UserState = {
  user: getUserPayloadFromToken(localStorage.getItem('trustid_access_token')),
  userRole: null,
  permissions: [],
  brandId: null,
  meLoaded: false,
  userProfile: null,
  userAdminBrand: null,
  accessToken: localStorage.getItem('trustid_access_token'),
  refreshToken: localStorage.getItem('trustid_refresh_token'),
}

export const useAuthStore = create<UserState & UserActions>((set) => ({

  ...initialUserState,

  setUserRole: (userRole) => set((state) => ({ ...state, userRole })),

  setPermissions: (permissions) => set((state) => ({ ...state, permissions })),

  setMe: (me) => set((state) => ({
    ...state,
    permissions: me.permissions,
    brandId: me.brand_id,
    meLoaded: true,
  })),

  setUser: (user: UserPayload | null) => set((state) => {
    return { ...state, user }
  }),

  setAccessToken: (token: string | null) => set((state) => {
    if (token) {
      const user = getUserPayloadFromToken(token);
      localStorage.setItem('trustid_access_token', token);
      return { ...state, accessToken: token, user };
    } else {
      localStorage.removeItem('trustid_access_token');
      return { ...state, accessToken: token, user: null };
    }
  }),

  setRefreshToken: (token: string | null) => set((state) => {
    if (token) {
      localStorage.setItem('trustid_refresh_token', token);
    } else {
      localStorage.removeItem('trustid_refresh_token')
    }
    return { ...state, refreshToken: token }
  }),

  logout: () => set(() => {
    localStorage.removeItem('trustid_access_token');
    localStorage.removeItem('trustid_refresh_token');
    window.location.pathname = '/login';
    return initialUserState;
  }),

  setUserProfile: (userProfile) => set((state) => ({ ...state, userProfile })),

  setUserAdminBrand: (userAdminBrand) => set((state) => ({ ...state, userAdminBrand }))

}));
